import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { ArrowRight, Shield, Activity, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const C_GREEN = '#00C853';
const C_AMBER = '#FFB000';
const C_RED   = '#FF3B30';

/**
 * Slice L · Public Trust Page · Cross-Venue Spread Pulse
 * Un-authenticated marketing surface — proves multi-venue routing edge.
 */
export default function TrustSpreads() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [symbol, setSymbol] = useState('BTCUSDT');
  const [symbolInput, setSymbolInput] = useState('BTCUSDT');

  const fetchOnce = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/public/cross-venue-spreads`, { params: { symbol } });
      setData(r.data);
      setErr('');
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  }, [symbol]);

  useEffect(() => { fetchOnce(); }, [fetchOnce]);
  useEffect(() => {
    const t = setInterval(fetchOnce, 60_000);
    return () => clearInterval(t);
  }, [fetchOnce]);

  const onSubmit = (e) => {
    e.preventDefault();
    const next = (symbolInput || '').trim().toUpperCase().replace(/[/\-_]/g, '');
    if (next) setSymbol(next);
  };

  return (
    <div data-testid="trust-spreads-page" style={{
      minHeight: '100vh', background: '#0a0d12', color: '#e6edf3',
      fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
    }}>
      {/* Hero */}
      <div style={{
        maxWidth: 1200, margin: '0 auto', padding: '60px 24px 28px',
      }}>
        <div style={{ fontSize: 11, letterSpacing: 4, color: '#5a6675', fontWeight: 700 }}>
          APEXFLOW · INSTITUTIONAL ROUTING · PUBLIC TRANSPARENCY
        </div>
        <h1 style={{
          fontSize: 56, lineHeight: 1.05, fontWeight: 800, margin: '14px 0 8px 0',
          letterSpacing: -1.5,
        }}>
          Cross-Venue<br />
          <span style={{ color: C_GREEN }}>Spread Pulse</span>
        </h1>
        <p style={{ fontSize: 16, color: '#8a96a6', maxWidth: 720, marginBottom: 24 }}>
          Live best-bid / best-ask across {data?.venue_count ?? 8} venues — refreshed every 60s,
          straight from each exchange's public REST surface. No auth, no aggregation, no edits.
          {' '}This is what our Smart Order Router sees in real time.
        </p>
        <form onSubmit={onSubmit} style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          <input
            data-testid="trust-symbol-input"
            value={symbolInput}
            onChange={(e) => setSymbolInput(e.target.value)}
            style={{
              padding: '10px 14px', fontSize: 14, fontWeight: 600, letterSpacing: 1,
              background: '#14171c', color: '#e6edf3',
              border: '1px solid #2a2f37', borderRadius: 6, width: 180,
              fontFamily: 'inherit',
            }}
          />
          <button type="submit" data-testid="trust-symbol-go"
                  style={{
                    padding: '10px 18px', fontSize: 11, fontWeight: 800, letterSpacing: 2,
                    background: C_GREEN, color: '#000', border: 0, borderRadius: 6,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                  }}>
            PULSE <ArrowRight size={14} />
          </button>
        </form>
      </div>

      {err && (
        <div data-testid="trust-error" style={{
          maxWidth: 1200, margin: '0 auto', padding: '12px 24px',
          color: C_RED, fontSize: 13,
        }}>{err}</div>
      )}

      {/* Headline */}
      {data && (
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '20px 24px' }}>
          <div data-testid="trust-headline" style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 14, marginBottom: 28,
          }}>
            <HeroTile label="SYMBOL" value={data.symbol} testid="trust-kpi-symbol" />
            <HeroTile label="MID PRICE" value={`$${data.mid?.toLocaleString()}`}
                      testid="trust-kpi-mid" />
            <HeroTile label="X-VENUE SPREAD"
                      value={`${data.spread_bps} bps`}
                      color={data.spread_bps < 0 ? C_GREEN : data.spread_bps < 5 ? C_GREEN : C_AMBER}
                      testid="trust-kpi-spread" />
            <HeroTile label="VENUES HEALTHY"
                      value={`${data.healthy_count}/${data.venue_count}`}
                      color={data.healthy_count === data.venue_count ? C_GREEN : C_AMBER}
                      testid="trust-kpi-healthy" />
          </div>

          <div data-testid="trust-best-callout" style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 28,
          }}>
            <BestSide kind="BID" label="HIGHEST BUY"
                      venue={data.best_bid?.display_name}
                      price={data.best_bid?.price} testid="trust-best-bid" />
            <BestSide kind="ASK" label="LOWEST SELL"
                      venue={data.best_ask?.display_name}
                      price={data.best_ask?.price} testid="trust-best-ask" />
          </div>

          {/* Venue table */}
          <div style={{
            background: '#0f1115', border: '1px solid #2a2f37', borderRadius: 8, padding: 18,
            marginBottom: 28,
          }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 2, color: '#5a6675',
                          marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Activity size={12} /> ALL VENUES
            </div>
            <table data-testid="trust-venues-table" style={{
              width: '100%', fontSize: 13, borderCollapse: 'collapse',
            }}>
              <thead>
                <tr style={{ color: '#5a6675', textAlign: 'left' }}>
                  <th style={th2}>VENUE</th>
                  <th style={th2}>STATUS</th>
                  <th style={th2}>BID</th>
                  <th style={th2}>ASK</th>
                  <th style={th2}>SPREAD</th>
                </tr>
              </thead>
              <tbody>
                {(data.venues || []).map((v) => {
                  const isBestBid = v.venue === data.best_bid?.venue;
                  const isBestAsk = v.venue === data.best_ask?.venue;
                  const venueSpread = v.ok && v.bid && v.ask
                    ? ((v.ask - v.bid) / ((v.ask + v.bid) / 2) * 10_000).toFixed(2)
                    : '—';
                  return (
                    <tr key={v.venue} data-testid={`trust-row-${v.venue}`}
                        style={{ borderTop: '1px solid #1f2329' }}>
                      <td style={{ ...td2, fontWeight: 700, letterSpacing: 1 }}>
                        {v.display_name?.toUpperCase()}
                      </td>
                      <td style={td2}>
                        <span style={{
                          fontSize: 10, padding: '2px 7px', borderRadius: 3,
                          background: v.ok ? C_GREEN : '#5a6675',
                          color: '#000', fontWeight: 800, letterSpacing: 1,
                        }}>{v.ok ? 'LIVE' : 'OFFLINE'}</span>
                      </td>
                      <td style={{ ...td2, color: isBestBid ? C_GREEN : 'inherit',
                                   fontWeight: isBestBid ? 800 : 400 }}>
                        {v.ok ? `$${v.bid?.toLocaleString(undefined, { maximumFractionDigits: 4 })}` : '—'}
                        {isBestBid && ' ★'}
                      </td>
                      <td style={{ ...td2, color: isBestAsk ? C_GREEN : 'inherit',
                                   fontWeight: isBestAsk ? 800 : 400 }}>
                        {v.ok ? `$${v.ask?.toLocaleString(undefined, { maximumFractionDigits: 4 })}` : '—'}
                        {isBestAsk && ' ★'}
                      </td>
                      <td style={td2}>{venueSpread} bps</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Safety footer */}
          <div data-testid="trust-safety" style={{
            padding: '14px 18px', borderRadius: 8,
            background: 'rgba(0,200,83,0.06)', border: `1px solid ${C_GREEN}`,
            fontSize: 12, color: '#8a96a6',
          }}>
            <Shield size={12} style={{ verticalAlign: 'middle', color: C_GREEN }} />
            {' '}<b style={{ color: '#e6edf3' }}>SAFETY POSTURE:</b>{' '}
            <code style={{ color: C_GREEN }}>EFFECTIVE_MODE = PAPER</code>{' · '}
            <code style={{ color: C_GREEN }}>LIVE_MODE_ENABLED = false</code>{' · '}
            Every signed live order placed through ApexFlow ships with an HMAC-SHA256 audit
            receipt (Phase R-TRANSPARENCY) tying the fill back to this exact cross-venue snapshot.
          </div>

          <div style={{ marginTop: 14, fontSize: 11, color: '#5a6675', textAlign: 'center' }}>
            Last refresh: {data.generated_at && new Date(data.generated_at).toLocaleString()}
            {' · auto-refresh every 60s · '}
            <a href="/" style={{ color: '#5a6675', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              apexflow <ExternalLink size={10} />
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

function HeroTile({ label, value, color, testid }) {
  return (
    <div data-testid={testid} style={{
      background: '#0f1115', border: '1px solid #2a2f37', borderRadius: 8, padding: '14px 16px',
    }}>
      <div style={{ fontSize: 10, color: '#5a6675', letterSpacing: 2, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: color || '#e6edf3', marginTop: 4 }}>
        {value}
      </div>
    </div>
  );
}

function BestSide({ kind, label, venue, price, testid }) {
  return (
    <div data-testid={testid} style={{
      background: '#0f1115', border: `1px solid ${C_GREEN}`, borderRadius: 8, padding: '20px 22px',
    }}>
      <div style={{ fontSize: 10, color: '#5a6675', letterSpacing: 2, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 800, color: C_GREEN, marginTop: 4 }}>
        ${price?.toLocaleString(undefined, { maximumFractionDigits: 4 })}
      </div>
      <div style={{ fontSize: 11, color: '#8a96a6', marginTop: 2, letterSpacing: 1 }}>
        on <b style={{ color: '#e6edf3' }}>{venue}</b> · {kind}
      </div>
    </div>
  );
}

const th2 = { padding: '8px 10px', fontSize: 10, letterSpacing: 2, fontWeight: 700 };
const td2 = { padding: '8px 10px', verticalAlign: 'middle' };
